/**
 * MotorMonitoringService — main-thread side.
 *
 * Manages a single Web Worker for live NATS data, and fetches
 * historical data from Doris (OLAP database) via NATS request/reply.
 *
 * Live:    Worker → discovery + deliverNew
 * History: Doris PC.sensor_history (hourly aggregated)
 * Detail:  Doris PC.sensor_history_detail (full resolution, chunked)
 */

import type {
  MotorState,
  MotorSignals,
  MotorHistoryPoint,
  MotorSnapshotEntry,
  WorkerToMainMsg,
  MainToWorkerMsg,
  WorkerDiscoveryMsg,
} from '../workers/motorMonitoring.types';
import { MOTOR_SIGNAL_SUFFIXES } from '../workers/motorMonitoring.types';
import { extractShortName } from '../workers/motorSubjectParser';
import MotorMonitoringWorker from '../workers/motorMonitoring.worker.ts?worker';
import { orgNatsClient, StatementIds } from './OrgNATSClient';
import { JETSTREAM_OP_TIMEOUT_MS } from '../utils/natsConfig';

// Re-export types so consumers keep the same import path
export type { MotorSignals, MotorHistoryPoint, MotorState } from '../workers/motorMonitoring.types';

export type MotorMapCallback = (motors: Map<string, MotorState>, replaying: boolean, replayProgress: number) => void;
export type StreamInfoCallback = (firstMessageTime: string | null, lastMessageTime: string | null) => void;

/** Name of the NATS JetStream stream that holds motor live data */
export const MOTOR_STREAM_NAME = 'FOCKE_TEST_LIVE';
/**
 * Name of the "current value" stream — always retains the last value per
 * subject, so the list shows the latest reading even when a signal hasn't
 * published within the live stream's retention window.
 */
export const MOTOR_CURRENT_STREAM_NAME = 'FOCKE_TEST_CURRENT';

// ─── Sensor name → MotorHistoryPoint field mapping ───────────────────────────

/** Maps sensor suffix (e.g. "_TorqueRmsValue") → MotorHistoryPoint field */
const SENSOR_SUFFIX_TO_HISTORY_FIELD = new Map<string, keyof MotorHistoryPoint>();
/** Maps bare signal name (e.g. "TorqueRmsValue") → field, for edge-16 "direct" schema */
const BARE_SENSOR_TO_HISTORY_FIELD = new Map<string, keyof MotorHistoryPoint>();
const CHART_FIELDS = new Set<keyof MotorHistoryPoint>([
  'torqueRms', 'torqueDc', 'lagDistanceDc', 'lagDistanceRms',
  'tempMotor', 'tempAmplifier', 'ambientTemperature', 'velocity', 'fbActHours',
]);
for (const [suffix, field] of Object.entries(MOTOR_SIGNAL_SUFFIXES)) {
  if (CHART_FIELDS.has(field as keyof MotorHistoryPoint)) {
    SENSOR_SUFFIX_TO_HISTORY_FIELD.set(suffix, field as keyof MotorHistoryPoint);
    // suffix "_TorqueRmsValue" → bare "TorqueRmsValue"
    BARE_SENSOR_TO_HISTORY_FIELD.set(suffix.slice(1), field as keyof MotorHistoryPoint);
  }
}

/**
 * Resolve a Doris sensor_name → chart field, tolerant of both schemas:
 *  - prefixed: "<motorId>_TorqueRmsValue"  (dotted/flat motor formats)
 *  - bare:     "TorqueRmsValue"            (edge-16 "direct" format, motorId = serial)
 */
function sensorNameToHistoryField(sensorName: string, motorId: string): keyof MotorHistoryPoint | undefined {
  // Schema A: Doris stores "<motorId>_<Signal>".
  if (sensorName.length > motorId.length && sensorName.startsWith(motorId)) {
    const field = SENSOR_SUFFIX_TO_HISTORY_FIELD.get(sensorName.slice(motorId.length));
    if (field) return field;
  }
  // Bare exact (edge-16 "direct" format).
  const exact = BARE_SENSOR_TO_HISTORY_FIELD.get(sensorName);
  if (exact) return exact;
  // Schema B: the signal is the last underscore segment, e.g.
  // "…_IoAxis_stState_TorqueRmsValue" → "TorqueRmsValue".
  const lastSeg = sensorName.slice(sensorName.lastIndexOf('_') + 1);
  return BARE_SENSOR_TO_HISTORY_FIELD.get(lastSeg);
}

/**
 * Compute the Doris query for a motorId, tolerant of both schemas.
 *  - Schema A ("P0655_F_ZIFO_MA12"): prefix = motorId, no axis filter.
 *  - Schema B ("P0655_F_ZFSB_fbCigFormation_MA03"): the grouped id doesn't
 *    prefix-match Doris names (which contain "…_fbAxis_MA03_Device_…"), so query
 *    by <module>_<component> and filter rows by the axis token.
 */
function dorisQueryForMotor(motorId: string): { prefix: string; axis: string | null } {
  const isSchemaB = /_fb[A-Za-z]/.test(motorId) && /_MA\d+$/.test(motorId);
  if (isSchemaB) {
    const axis = motorId.match(/MA\d+$/)?.[0] ?? null;
    const prefix = motorId.replace(/_MA\d+$/, ''); // <module>_<component>
    return { prefix, axis };
  }
  return { prefix: motorId, axis: null };
}

/**
 * Reconstruct the GROUPED motorId (matching the live worker's grouping) from a
 * Doris `sensor_name` (underscore-joined full path + `_<Signal>`), and return
 * its trailing signal segment. Returns null for non-motor sensors (no MAxx).
 *
 *   "P0655_F_ZIFO_MA12_TorqueRmsValue"                                  → { motorId: "P0655_F_ZIFO_MA12", signal: "TorqueRmsValue" }
 *   "P0655_F_ZFSB_fbCigFormation_fbAxis_MA03_Device_IoAxis_stState_TorqueRmsValue"
 *                                                                       → { motorId: "P0655_F_ZFSB_fbCigFormation_MA03", signal: "TorqueRmsValue" }
 *   "P0655_F_ZFSB_fbCigFormation___FB…OPERATINGHOURS…_MA03_fbOperatingHoursData_Hours"
 *                                                                       → { motorId: "P0655_F_ZFSB_fbCigFormation_MA03", signal: "Hours" }
 */
function groupedMotorIdFromDorisSensor(sensorName: string): { motorId: string; signal: string } | null {
  const axisM = sensorName.match(/MA\d+/);
  if (!axisM) return null; // VM / encoder / infrastructure → not a motor
  const axis = axisM[0];

  let prefix: string;
  const fbAxisIdx = sensorName.indexOf('_fbAxis_');
  const fbIdx = sensorName.indexOf('___FB'); // operating-hours wrapper
  if (fbAxisIdx !== -1) {
    prefix = sensorName.slice(0, fbAxisIdx);
  } else if (fbIdx !== -1) {
    prefix = sensorName.slice(0, fbIdx);
  } else {
    // Schema A / axis-last: cut right before the axis token.
    const ai = sensorName.indexOf(`_${axis}`);
    prefix = ai !== -1 ? sensorName.slice(0, ai) : sensorName;
  }
  const motorId = `${prefix}_${axis}`;
  if (!motorId.includes('_F_')) return null;

  const signal = sensorName.slice(sensorName.lastIndexOf('_') + 1);
  return { motorId, signal };
}

/** Bare Doris signal name → MotorSignals field, for latest-value KPI tiles. */
const DORIS_SIGNAL_TO_SIGNAL_FIELD = new Map<string, keyof import('../workers/motorMonitoring.types').MotorSignals>([
  ['TorqueRmsValue', 'torqueRms'],
  ['TorqueDcValue', 'torqueDc'],
  ['LagDistanceRmsValue', 'lagDistanceRms'],
  ['LagDistanceDcValue', 'lagDistanceDc'],
  ['TempMotor', 'tempMotor'],
  ['TempAmplifier', 'tempAmplifier'],
  ['Days', 'fbDays'],
  ['Hours', 'fbHours'],
  ['Minutes', 'fbMinutes'],
  ['MachineActVelocity', 'velocity'],
  ['MachineVelocity', 'velocity'],
]);

/** Doris sensor_history row (hourly aggregated) */
interface SensorHistoryRow {
  timestamp: string;
  sensor_name: string;
  avg_value: number;
  min_value: number;
  max_value: number;
  sample_count: number;
}

/** Doris sensor_history_detail row (full resolution) */
interface SensorDetailRow {
  timestamp: string;
  sensor_name: string;
  value: number;
}

/**
 * Group Doris sensor rows for the WHOLE machine into axisId → points, merging
 * signals that share a timestamp into one point. Shared by the aggregated and
 * full-resolution all-axes loaders, which differ only in the value column.
 */
function groupRowsByAxis<T extends { timestamp: string; sensor_name: string }>(
  rows: T[],
  getValue: (row: T) => number,
): Map<string, MotorHistoryPoint[]> {
  const byAxis = new Map<string, Map<number, MotorHistoryPoint>>();

  for (const row of rows) {
    const g = groupedMotorIdFromDorisSensor(row.sensor_name);
    if (!g) continue;
    const field = DORIS_SIGNAL_TO_SIGNAL_FIELD.get(g.signal) as
      | keyof MotorHistoryPoint
      | undefined;
    if (!field || !CHART_FIELDS.has(field)) continue;
    const ts = new Date(row.timestamp).getTime();
    if (Number.isNaN(ts)) continue;

    let series = byAxis.get(g.motorId);
    if (!series) {
      series = new Map();
      byAxis.set(g.motorId, series);
    }
    let point = series.get(ts);
    if (!point) {
      point = { timestamp: ts };
      series.set(ts, point);
    }
    (point as unknown as Record<string, unknown>)[field] = getValue(row);
  }

  const result = new Map<string, MotorHistoryPoint[]>();
  byAxis.forEach((points, axisId) => {
    result.set(
      axisId,
      Array.from(points.values()).sort((a, b) => a.timestamp - b.timestamp),
    );
  });
  return result;
}

export class MotorMonitoringService {
  private liveWorker: Worker | null = null;
  private motors: Map<string, MotorState> = new Map();
  private callbacks: Set<MotorMapCallback> = new Set();
  private streamInfoCallbacks: Set<StreamInfoCallback> = new Set();
  private pendingTeardown: ReturnType<typeof setTimeout> | null = null;

  /** True while detail data is being fetched from Doris */
  detailLoading = false;

  // History replay state (from history worker)
  private historyReplaying = false;
  private historyReplayProgress = 0;

  // Cached stream info
  private firstMessageTime: string | null = null;
  private lastMessageTime: string | null = null;

  // Parameters needed to (re-)start workers
  private lastMachineBK: string | null = null;
  private lastToken: string | null = null;
  private lastStartTime: string | undefined = undefined;
  private lastSampleIntervalMs: number | undefined = undefined;
  private selectedMotorId: string | null = null;

  /** Motor ID → NATS subject prefix (new format only, from discovery) */
  private motorSubjectPrefixes = new Map<string, string>();

  /** Guards against duplicate Doris list-discovery for the same range. */
  private dorisListDiscoveryInFlight = false;

  /** True when showing a historical range: Doris only, no live NATS worker. */
  private dorisMode = false;

  async subscribe(
    _orgAlias: string,
    machineBK: string,
    callback: MotorMapCallback,
    startTime?: string,
    sampleIntervalMs?: number,
    /**
     * Auth JWT to use for Doris (range) queries. Required when there is no live
     * NATS activity, because natsClient.lastToken is only populated once the
     * live socket has authenticated. Falls back to natsClient.lastToken.
     */
    token?: string,
  ): Promise<void> {
    if (this.pendingTeardown !== null) {
      clearTimeout(this.pendingTeardown);
      this.pendingTeardown = null;
    }

    this.callbacks.add(callback);
    this.lastMachineBK = machineBK;
    this.lastSampleIntervalMs = sampleIntervalMs;
    this.lastStartTime = startTime;
    if (token) this.lastToken = token;

    if (this.liveWorker || this.dorisMode) {
      // Already active — just send current state
      callback(new Map(this.motors), this.historyReplaying, this.historyReplayProgress);
      return;
    }

    if (!this.lastToken) {
      const { natsClient } = await import('./NATSClient');
      this.lastToken = (natsClient as any).lastToken ?? '';
    }

    // Load discovery cache from localStorage (1-hour TTL)
    this.loadDiscoveryCache();

    if (startTime) {
      // Historical range → Doris ONLY (mirrors energy). No live NATS worker.
      // The two string fields (Part No / Motor Type) don't exist in Doris, so
      // they're simply absent in range mode; all numeric data comes from Doris.
      this.dorisMode = true;
      this.historyReplaying = true;
      this.callbacks.forEach(cb => cb(new Map(this.motors), true, 0));
      void this.discoverMotorsFromDoris(startTime);
      // Also attach the worker: it reads the retained "current" stream, which
      // is the only source for Motor Type / Part No. (absent from Doris).
      this.spawnWorkers();
    } else {
      // Live → NATS worker ONLY (provides live values + string metadata).
      this.spawnWorkers();
    }
  }

  /**
   * Switch time range. Live (undefined) → NATS worker only; a date range →
   * Doris only (mirrors energy: numeric data from Doris, string metadata absent).
   */
  setDateRange(startTime: string | undefined, sampleIntervalMs: number | undefined): void {
    if (startTime === this.lastStartTime && sampleIntervalMs === this.lastSampleIntervalMs) return;
    this.lastStartTime = startTime;
    this.lastSampleIntervalMs = sampleIntervalMs;

    if (startTime) {
      // ── Historical → numbers from Doris; keep the worker for metadata ──
      this.dorisMode = true;
      this.motors.clear();
      this.motorSubjectPrefixes.clear();
      this.historyReplaying = true;
      this.historyReplayProgress = 0;
      this.callbacks.forEach(cb => cb(new Map(this.motors), true, 0));
      void this.discoverMotorsFromDoris(startTime).then(() => {
        if (this.selectedMotorId) this.fetchSensorHistory(this.selectedMotorId, startTime);
      });
      if (!this.liveWorker) this.spawnWorkers();
    } else {
      // ── Live → NATS worker ONLY ────────────────────────────────────────
      this.dorisMode = false;
      this.motors.clear();
      this.callbacks.forEach(cb => cb(new Map(this.motors), false, 100));
      if (!this.liveWorker) this.spawnWorkers();
    }
  }

  onStreamInfo(callback: StreamInfoCallback): void {
    this.streamInfoCallbacks.add(callback);
    if (this.firstMessageTime !== null || this.lastMessageTime !== null) {
      callback(this.firstMessageTime, this.lastMessageTime);
    }
  }

  offStreamInfo(callback: StreamInfoCallback): void {
    this.streamInfoCallbacks.delete(callback);
  }

  /**
   * Fetch full-resolution detail data from Doris for a zoom window.
   * Merges into motor.history and notifies callbacks.
   */
  loadRangeDetail(startIso: string, endIso: string): void {
    if (!this.selectedMotorId || !this.lastToken) return;
    this.detailLoading = true;
    // Notify so UI can show loading state
    const snap = new Map(this.motors);
    this.callbacks.forEach(cb => cb(snap, false, 100));
    this.fetchSensorDetail(this.selectedMotorId, startIso, endIso);
  }

  /**
   * Set the selected motor for chart history accumulation.
   * Fetches overview history from Doris for the selected motor.
   */
  setSelectedMotor(motorId: string | null): void {
    if (motorId === this.selectedMotorId) return;
    const previousMotorId = this.selectedMotorId;
    this.selectedMotorId = motorId;

    // Tell the live worker which motor to accumulate chart history for
    if (this.liveWorker) {
      const motorPrefix = motorId
        ? this.motorSubjectPrefixes.get(motorId)
        : undefined;
      const msg: MainToWorkerMsg = {
        type: 'SET_SELECTED_MOTOR',
        motorId,
        motorSubjectPrefix: motorPrefix,
      };
      this.liveWorker.postMessage(msg);
    }

    // Clear chart history of the previously selected motor — it's no longer
    // displayed and keeping thousands of points wastes memory.
    if (previousMotorId) {
      const prev = this.motors.get(previousMotorId);
      if (prev) prev.history = [];
    }

    // Enrich from Doris if a motor is now selected. In live mode there is no
    // date range, so use a recent default window (the live worker no longer
    // replays history — see spawnWorkers skipReplay); live points from
    // deliverNew append on top.
    if (motorId) {
      const historyStart =
        this.lastStartTime ??
        new Date(Date.now() - MotorMonitoringService.LIVE_CHART_HISTORY_MS).toISOString();
      this.fetchSensorHistory(motorId, historyStart);
    } else {
      const snapshot = new Map(this.motors);
      this.callbacks.forEach(cb => cb(snapshot, false, 100));
    }
  }

  /** Recent window loaded from Doris for a live chart when no range is set. */
  private static LIVE_CHART_HISTORY_MS = 3 * 60 * 60 * 1000; // 3 hours

  unsubscribe(callback: MotorMapCallback): void {
    this.callbacks.delete(callback);
    if (this.callbacks.size === 0) {
      this.pendingTeardown = setTimeout(() => {
        this.pendingTeardown = null;
        if (this.callbacks.size === 0) this.teardownAllWorkers(true);
      }, 50);
    }
  }

  /**
   * Full cleanup — terminates workers, clears all data, removes from instance map.
   * Call when the component unmounts so memory is released.
   */
  dispose(): void {
    if (this.pendingTeardown !== null) {
      clearTimeout(this.pendingTeardown);
      this.pendingTeardown = null;
    }
    this.teardownAllWorkers(true);
    this.callbacks.clear();
    this.streamInfoCallbacks.clear();
    this.selectedMotorId = null;
    this.lastMachineBK = null;
    this.lastToken = null;
    this.lastStartTime = undefined;
    this.lastSampleIntervalMs = undefined;
    this.firstMessageTime = null;
    this.lastMessageTime = null;
  }

  getMotors(): Map<string, MotorState> {
    return new Map(this.motors);
  }

  // ─── Worker lifecycle ──────────────────────────────────────────────────────

  private spawnWorkers(): void {
    const serverUrl = import.meta.env.VITE_NATS_URL || 'wss://natsws.enlixy.com:443';
    const subject = `edge-data.live.*.${this.lastMachineBK}.>`;
    // Last-value source (always retained) for the discovery pass.
    const currentSubject = `edge-data.current.*.${this.lastMachineBK}.>`;

    console.log(`🔧 MotorMonitoringService: starting worker for ${subject}`);

    if (!this.liveWorker) {
      this.liveWorker = new MotorMonitoringWorker();
      this.liveWorker.onmessage = (event: MessageEvent<WorkerToMainMsg>) => {
        this.handleLiveWorkerMessage(event.data);
      };
      this.liveWorker.onerror = (err) => {
        console.error('MotorMonitoringService: worker error', err);
      };

      const liveMsg: MainToWorkerMsg = {
        type: 'CONNECT',
        serverUrl,
        token: this.lastToken ?? '',
        subject,
        streamName: MOTOR_STREAM_NAME,
        // Discovery reads the always-retained last value from the current stream;
        // live updates still come from the live stream.
        currentSubject,
        currentStreamName: MOTOR_CURRENT_STREAM_NAME,
        // List (cards/tree) only needs latest values from discovery + deliverNew.
        // Chart history is loaded from Doris on demand (see setSelectedMotor),
        // so skip the expensive full-stream replay.
        skipReplay: true,
      };
      this.liveWorker.postMessage(liveMsg);
    }
  }

  private terminateWorker(worker: Worker): void {
    const msg: MainToWorkerMsg = { type: 'DISCONNECT' };
    worker.postMessage(msg);
    setTimeout(() => worker.terminate(), 500);
  }

  private teardownAllWorkers(clearMotors: boolean): void {
    if (this.liveWorker) {
      this.terminateWorker(this.liveWorker);
      this.liveWorker = null;
    }
    this.historyReplaying = false;
    this.historyReplayProgress = 0;
    if (clearMotors) this.motors.clear();
    this.motorSubjectPrefixes.clear();
    this.dorisMode = false;
  }

  // ─── Live worker messages ──────────────────────────────────────────────────



  private handleLiveWorkerMessage(msg: WorkerToMainMsg): void {
    switch (msg.type) {
      case 'CONNECTED':
        console.log('MotorMonitoringService: live worker connected to NATS');
        break;

      case 'MOTORS_SNAPSHOT': {
        // Lightweight update: snapshot carries signals + latest point only
        const snapshotEntries = msg.motors as [string, MotorSnapshotEntry][];
        // Range mode: the list and all numbers come from Doris. The worker is
        // only here to supply the string metadata Doris doesn't store, so take
        // those fields and ignore live values/history.
        if (this.dorisMode) {
          this.mergeMetadataFromSnapshot(snapshotEntries);
          break;
        }
        if (this.motors.size === 0 && snapshotEntries.length > 0) {
          console.log(`MotorMonitoringService: first snapshot with ${snapshotEntries.length} motors`);
        }
        for (const [motorId, entry] of snapshotEntries) {
          let motor = this.motors.get(motorId);
          if (!motor) {
            // New motor discovered
            motor = {
              id: entry.id,
              shortName: entry.shortName,
              signals: entry.signals,
              history: [],
              lastUpdate: entry.lastUpdate,
            };
            this.motors.set(motorId, motor);
          } else {
            // Update signals and timestamp in place (no copy)
            motor.signals = entry.signals;
            motor.lastUpdate = entry.lastUpdate;
          }
          // Append the new live history point (if any)
          if (entry.latestPoint) {
            motor.history.push(entry.latestPoint);
          }
        }
        const snapshot = new Map(this.motors);
        this.callbacks.forEach(cb => cb(snapshot, this.historyReplaying, this.historyReplayProgress));
        break;
      }

      case 'HISTORY_BATCH': {
        this.historyReplaying = msg.replaying;
        this.historyReplayProgress = msg.replayProgress;

        // Merge history patches from the worker (full replay data)
        if (msg.patches && msg.patches.length > 0) {
          for (const { motorId, points } of msg.patches) {
            let motor = this.motors.get(motorId);
            if (!motor) {
              motor = {
                id: motorId,
                shortName: extractShortName(motorId),
                signals: {} as any,
                history: [],
                lastUpdate: Date.now(),
              };
              this.motors.set(motorId, motor);
            }
            // Replace history with the full replay data
            motor.history = points;
          }
        }

        // Only notify on final batch (replaying=false) or valid progress update
        if (!msg.replaying || msg.patches?.length === 0) {
          const snap = new Map(this.motors);
          this.callbacks.forEach(cb => cb(snap, msg.replaying, msg.replayProgress));
        }
        break;
      }

      case 'HISTORY_DONE': {
        // Legacy — replay complete is now signaled by HISTORY_BATCH with replaying=false
        this.historyReplaying = false;
        this.historyReplayProgress = 100;
        console.log('MotorMonitoringService: replay complete');
        break;
      }

      case 'RANGE_DETAIL_DATA':
        // Detail data from worker (unused — we use Doris instead)
        break;

      case 'STREAM_INFO':
        this.firstMessageTime = msg.firstMessageTime;
        this.lastMessageTime = msg.lastMessageTime;
        this.streamInfoCallbacks.forEach(cb => cb(msg.firstMessageTime, msg.lastMessageTime));
        break;

      case 'DISCOVERY':
        this.handleDiscovery(msg as WorkerDiscoveryMsg);
        break;

      case 'DISCONNECTED':
        console.log('MotorMonitoringService: worker disconnected', msg.error ?? '');
        break;

      case 'ERROR': {
        console.error('MotorMonitoringService: live worker error', msg.error);
        if (this.liveWorker) {
          const dying = this.liveWorker;
          this.liveWorker = null;
          setTimeout(() => dying.terminate(), 200);
        }
        if (this.callbacks.size > 0) {
          setTimeout(() => {
            if (this.callbacks.size > 0 && !this.liveWorker) this.spawnWorkers();
          }, 2000);
        }
        break;
      }

      case 'DEBUG_LOG':
        console.log(`[Worker] ${msg.message}`);
        break;
    }
  }

  /**
   * Signal fields that only exist in NATS (never in Doris), so they must be
   * carried over from the retained "current" stream in range mode.
   */
  private static METADATA_STRING_FIELDS: (keyof MotorSignals)[] = [
    'motorTyp',
    'motorPartNo',
  ];

  /**
   * Copy NATS-only string metadata (Motor Type / Part No.) onto the Doris-built
   * motor list. Only fills existing motors — in range mode the list itself is
   * owned by Doris — and never touches numeric signals or history.
   */
  private mergeMetadataFromSnapshot(entries: [string, MotorSnapshotEntry][]): void {
    let changed = false;
    for (const [motorId, entry] of entries) {
      const motor = this.motors.get(motorId);
      if (!motor || !entry.signals) continue;
      for (const field of MotorMonitoringService.METADATA_STRING_FIELDS) {
        const value = entry.signals[field];
        if (typeof value === 'string' && value.trim() && motor.signals[field] !== value) {
          (motor.signals as Record<string, unknown>)[field] = value;
          changed = true;
        }
      }
    }
    if (!changed) return;
    const snap = new Map(this.motors);
    this.callbacks.forEach(cb => cb(snap, this.historyReplaying, this.historyReplayProgress));
  }

  // ─── Discovery cache ────────────────────────────────────────────────────────

  private static DISCOVERY_CACHE_TTL_MS = 3600_000; // 1 hour

  private handleDiscovery(msg: WorkerDiscoveryMsg): void {
    console.log(`MotorMonitoringService: DISCOVERY — format=${msg.format}, motors=${msg.motors.length}`, msg.motors.map(m => m.id));
    this.motorSubjectPrefixes.clear();
    for (const motor of msg.motors) {
      if (motor.subjectPrefix) {
        this.motorSubjectPrefixes.set(motor.id, motor.subjectPrefix);
      }
    }
    this.saveDiscoveryCache(msg);

    // Empty/expired stream → the worker found no motors. If we're looking at a
    // historical range, still populate the list from Doris so the charts are
    // reachable.
    if (msg.motors.length === 0 && this.motors.size === 0 && this.lastStartTime) {
      void this.discoverMotorsFromDoris(this.lastStartTime);
    }
  }

  /**
   * Populate the motor list + latest KPI values from Doris (range mode).
   * Builds GROUPED motor ids matching the live worker, and fills each motor's
   * numeric signals from the most recent hourly value so the cards' KPI tiles
   * show. Operating hours are derived from the Days/Hours/Minutes breakdown.
   */
  private async discoverMotorsFromDoris(startTime: string): Promise<void> {
    if (!this.lastToken || !this.lastMachineBK) {
      // Can't query Doris without a token/serial — clear the loading state so
      // the UI shows an empty result instead of spinning forever.
      this.historyReplaying = false;
      this.historyReplayProgress = 100;
      this.callbacks.forEach(cb => cb(new Map(this.motors), false, 100));
      return;
    }
    if (this.dorisListDiscoveryInFlight) return;
    this.dorisListDiscoveryInFlight = true;
    try {
      const response = await orgNatsClient.requestChunked<SensorHistoryRow>(
        StatementIds.SENSOR_HISTORY_AXIS,
        {
          serialno: this.lastMachineBK,
          sensor_prefix: '%',
          start: startTime,
          end: new Date().toISOString(),
        },
        this.lastToken,
        { timeout: JETSTREAM_OP_TIMEOUT_MS },
      );
      if (!response.success || !response.data || response.data.length === 0) return;

      // Latest numeric value per (motorId, signal-field) across the range.
      const latest = new Map<string, Map<keyof import('../workers/motorMonitoring.types').MotorSignals, { t: number; v: number }>>();

      for (const row of response.data) {
        const g = groupedMotorIdFromDorisSensor(row.sensor_name);
        if (!g) continue;
        if (!this.motors.has(g.motorId)) {
          this.motors.set(g.motorId, {
            id: g.motorId,
            shortName: extractShortName(g.motorId),
            signals: {} as any,
            history: [],
            lastUpdate: 0,
          });
        }
        const field = DORIS_SIGNAL_TO_SIGNAL_FIELD.get(g.signal);
        if (!field) continue;
        const t = new Date(row.timestamp).getTime();
        if (Number.isNaN(t)) continue;
        let m = latest.get(g.motorId);
        if (!m) { m = new Map(); latest.set(g.motorId, m); }
        const cur = m.get(field);
        if (!cur || t > cur.t) m.set(field, { t, v: row.avg_value });
      }

      // Apply latest values → signals; derive total operating hours.
      latest.forEach((fields, motorId) => {
        const motor = this.motors.get(motorId);
        if (!motor) return;
        fields.forEach((pt, field) => {
          (motor.signals as Record<string, unknown>)[field] = pt.v;
        });
        const s = motor.signals;
        if (s.fbDays !== undefined || s.fbHours !== undefined || s.fbMinutes !== undefined) {
          s.fbActHours = (s.fbDays ?? 0) * 24 + (s.fbHours ?? 0) + (s.fbMinutes ?? 0) / 60;
        }
      });

      console.log(`MotorMonitoringService: Doris discovery found ${this.motors.size} motor(s) from ${response.data.length} rows`);
      const snap = new Map(this.motors);
      this.callbacks.forEach(cb => cb(snap, false, 100));
    } catch (e) {
      console.warn('MotorMonitoringService: Doris motor discovery failed', e);
    } finally {
      this.dorisListDiscoveryInFlight = false;
      // Always clear the loading state, even if no motors were found.
      this.historyReplaying = false;
      this.historyReplayProgress = 100;
      this.callbacks.forEach(cb => cb(new Map(this.motors), false, 100));
    }
  }

  private get discoveryCacheKey(): string {
    return `motor-discovery:${this.lastMachineBK}`;
  }

  private loadDiscoveryCache(): void {
    try {
      const raw = localStorage.getItem(this.discoveryCacheKey);
      if (!raw) return;
      const cache = JSON.parse(raw) as { ts: number; format: 'old' | 'new'; motors: WorkerDiscoveryMsg['motors'] };
      if (Date.now() - cache.ts > MotorMonitoringService.DISCOVERY_CACHE_TTL_MS) {
        localStorage.removeItem(this.discoveryCacheKey);
        return;
      }
      this.motorSubjectPrefixes.clear();
      for (const motor of cache.motors) {
        if (motor.subjectPrefix) {
          this.motorSubjectPrefixes.set(motor.id, motor.subjectPrefix);
        }
      }
    } catch {
      // Corrupted cache — ignore
    }
  }

  private saveDiscoveryCache(msg: WorkerDiscoveryMsg): void {
    try {
      const cache = { ts: Date.now(), format: msg.format, motors: msg.motors };
      localStorage.setItem(this.discoveryCacheKey, JSON.stringify(cache));
    } catch {
      // localStorage full or unavailable — ignore
    }
  }

  // ─── Doris data fetching ────────────────────────────────────────────────────

  /**
   * Convert Doris sensor_history rows (hourly aggregated) into MotorHistoryPoint[].
   * Rows are grouped by timestamp; avg_value becomes the chart field.
   */
  private convertHistoryRows(rows: SensorHistoryRow[], motorId: string, axis: string | null = null): MotorHistoryPoint[] {
    const axisToken = axis ? `_${axis}_` : null;
    const byTs = new Map<number, MotorHistoryPoint>();
    for (const row of rows) {
      // Schema B: the query is scoped to the component, so filter to the axis.
      if (axisToken && !row.sensor_name.includes(axisToken)) continue;
      const field = sensorNameToHistoryField(row.sensor_name, motorId);
      if (!field) continue;
      const ts = new Date(row.timestamp).getTime();
      let point = byTs.get(ts);
      if (!point) {
        point = { timestamp: ts };
        byTs.set(ts, point);
      }
      (point as unknown as Record<string, unknown>)[field] = row.avg_value;
    }
    return Array.from(byTs.values()).sort((a, b) => a.timestamp - b.timestamp);
  }

  /**
   * Convert Doris sensor_history_detail rows (full resolution) into MotorHistoryPoint[].
   */
  private convertDetailRows(rows: SensorDetailRow[], motorId: string, axis: string | null = null): MotorHistoryPoint[] {
    const axisToken = axis ? `_${axis}_` : null;
    const byTs = new Map<number, MotorHistoryPoint>();
    for (const row of rows) {
      if (axisToken && !row.sensor_name.includes(axisToken)) continue;
      const field = sensorNameToHistoryField(row.sensor_name, motorId);
      if (!field) continue;
      const ts = new Date(row.timestamp).getTime();
      let point = byTs.get(ts);
      if (!point) {
        point = { timestamp: ts };
        byTs.set(ts, point);
      }
      (point as unknown as Record<string, unknown>)[field] = row.value;
    }
    return Array.from(byTs.values()).sort((a, b) => a.timestamp - b.timestamp);
  }

  /**
   * Load hourly-aggregated history for EVERY axis on the machine in one Doris
   * query (`sensor_prefix: '%'`), grouped by axis.
   *
   * Used by the Servo "Historical Values" screen, which overlays all axes on a
   * shared chart per signal — so it needs the whole machine at once rather than
   * the single selected axis {@link fetchSensorHistory} returns.
   *
   * @returns axisId → chronologically sorted points (empty map on failure).
   */
  async loadAllAxesHistory(
    startTime: string,
    endTime?: string,
  ): Promise<Map<string, MotorHistoryPoint[]>> {
    const result = new Map<string, MotorHistoryPoint[]>();
    if (!this.lastToken || !this.lastMachineBK) return result;

    let response;
    try {
      response = await orgNatsClient.requestChunked<SensorHistoryRow>(
        StatementIds.SENSOR_HISTORY_AXIS,
        {
          serialno: this.lastMachineBK,
          sensor_prefix: '%',
          start: startTime,
          end: endTime ?? new Date().toISOString(),
        },
        this.lastToken,
        { timeout: JETSTREAM_OP_TIMEOUT_MS },
      );
    } catch (err) {
      console.error('MotorMonitoringService: all-axes history error', err);
      return result;
    }

    if (!response.success || !response.data) {
      console.warn('MotorMonitoringService: all-axes history failed', response.error);
      return result;
    }

    const map = groupRowsByAxis(response.data, r => r.avg_value);
    console.log(
      `MotorMonitoringService: all-axes history loaded ${map.size} axes from ${response.data.length} rows`,
    );
    return map;
  }

  /**
   * Full-resolution counterpart of {@link loadAllAxesHistory} for a zoom window
   * — the all-axes equivalent of {@link fetchSensorDetail}. Lets the Servo
   * history screen offer the same "Full Resolution" action the motor charts have.
   *
   * @returns axisId → chronologically sorted points (empty map on failure).
   */
  async loadAllAxesDetail(
    startIso: string,
    endIso: string,
  ): Promise<Map<string, MotorHistoryPoint[]>> {
    const result = new Map<string, MotorHistoryPoint[]>();
    if (!this.lastToken || !this.lastMachineBK) return result;

    let response;
    try {
      response = await orgNatsClient.requestChunked<SensorDetailRow>(
        StatementIds.SENSOR_HISTORY_AXIS_DETAIL,
        {
          serialno: this.lastMachineBK,
          sensor_prefix: '%',
          start: startIso,
          end: endIso,
        },
        this.lastToken,
        { timeout: JETSTREAM_OP_TIMEOUT_MS },
      );
    } catch (err) {
      console.error('MotorMonitoringService: all-axes detail error', err);
      return result;
    }

    if (!response.success || !response.data) {
      console.warn('MotorMonitoringService: all-axes detail failed', response.error);
      return result;
    }

    const map = groupRowsByAxis(response.data, r => r.value);
    console.log(
      `MotorMonitoringService: all-axes detail loaded ${map.size} axes from ${response.data.length} rows`,
    );
    return map;
  }

  /**
   * Fetch hourly-aggregated history from Doris and merge into motor history.
   */
  private async fetchSensorHistory(motorId: string, startTime: string): Promise<void> {
    if (!this.lastToken || !this.lastMachineBK) return;

    this.historyReplaying = true;
    this.historyReplayProgress = 0;
    const snapshot1 = new Map(this.motors);
    this.callbacks.forEach(cb => cb(snapshot1, true, 0));

    try {
      const { prefix, axis } = dorisQueryForMotor(motorId);
      const response = await orgNatsClient.requestChunked<SensorHistoryRow>(
        StatementIds.SENSOR_HISTORY_AXIS,
        {
          serialno: this.lastMachineBK,
          sensor_prefix: `${prefix}%`,
          start: startTime,
          end: new Date().toISOString(),
        },
        this.lastToken,
        { timeout: JETSTREAM_OP_TIMEOUT_MS },
      );

      if (!response.success || !response.data) {
        console.warn('MotorMonitoringService: sensor_history failed', response.error);
        return;
      }

      const points = this.convertHistoryRows(response.data, motorId, axis);
      const motor = this.motors.get(motorId);
      if (motor) {
        // Prepend Doris history before any live points
        const liveStartTs = motor.history[0]?.timestamp ?? Infinity;
        const histPoints = points.filter(p => p.timestamp < liveStartTs);
        motor.history = [...histPoints, ...motor.history];
      }

      console.log(`MotorMonitoringService: sensor_history loaded ${points.length} points for ${motorId}`);
    } catch (err) {
      console.error('MotorMonitoringService: sensor_history error', err);
    } finally {
      this.historyReplaying = false;
      this.historyReplayProgress = 100;
      const snapshot2 = new Map(this.motors);
      this.callbacks.forEach(cb => cb(snapshot2, false, 100));
    }
  }

  /**
   * Fetch full-resolution detail data from Doris (chunked for large responses).
   */
  private async fetchSensorDetail(motorId: string, startIso: string, endIso: string): Promise<void> {
    if (!this.lastToken || !this.lastMachineBK) return;

    try {
      const { prefix, axis } = dorisQueryForMotor(motorId);
      const response = await orgNatsClient.requestChunked<SensorDetailRow>(
        StatementIds.SENSOR_HISTORY_AXIS_DETAIL,
        {
          serialno: this.lastMachineBK,
          sensor_prefix: `${prefix}%`,
          start: startIso,
          end: endIso,
        },
        this.lastToken,
        { timeout: JETSTREAM_OP_TIMEOUT_MS },
      );

      if (!response.success || !response.data) {
        console.warn('MotorMonitoringService: sensor_history_detail failed', response.error);
        return;
      }

      const detailPoints = this.convertDetailRows(response.data, motorId, axis);
      console.log(`MotorMonitoringService: sensor_history_detail loaded ${detailPoints.length} points for ${motorId}`);

      // Merge detail points into motor.history (sorted, deduped)
      if (detailPoints.length > 0) {
        const motor = this.motors.get(motorId);
        if (motor) {
          const merged = [...motor.history, ...detailPoints]
            .sort((a, b) => a.timestamp - b.timestamp)
            .filter((p, i, arr) => i === 0 || p.timestamp !== arr[i - 1].timestamp);
          motor.history = merged;
        }
      }
    } catch (err) {
      console.error('MotorMonitoringService: sensor_history_detail error', err);
    } finally {
      this.detailLoading = false;
      const snap = new Map(this.motors);
      this.callbacks.forEach(cb => cb(snap, false, 100));
    }
  }
}

// ─── One instance per machine BK ──────────────────────────────────────────────

const instances = new Map<string, MotorMonitoringService>();

export function getMotorMonitoringService(machineBK: string): MotorMonitoringService {
  if (!instances.has(machineBK)) {
    instances.set(machineBK, new MotorMonitoringService());
  }
  return instances.get(machineBK)!;
}

/** Get existing service without creating a new one. Returns undefined if not found. */
export function peekMotorMonitoringService(machineBK: string): MotorMonitoringService | undefined {
  return instances.get(machineBK);
}

/** Dispose and remove the service for a machine. Call on page unmount. */
export function destroyMotorMonitoringService(machineBK: string): void {
  const service = instances.get(machineBK);
  if (service) {
    service.dispose();
    instances.delete(machineBK);
  }
}
